import {
  MAX_DOCUMENT_FILE_SIZE,
  MAX_IMAGE_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE,
  VIDEO_DURATION_LIMIT_IN_SECONDS,
} from "constants/common";
import { MediaType } from "constants/enums";
import { FileWithMetadata } from "libs/directUpload";
import { ErrorCode } from "react-dropzone";
import toast from "react-hot-toast";

import { getVideoMetadata } from "./methods";

export const handleUploadFileError = (
  errorMessage: string,
  errorCode: ErrorCode | string,
  allowedMediaTypes: MediaType[],
  customMaxFileSize?: number,
) => {
  if (errorCode === ErrorCode.TooManyFiles) {
    return toast.error("Maximum files limit has been exceeded!");
  }

  if (errorCode === ErrorCode.FileInvalidType) {
    return toast.error("This file type is not supported!");
  }

  const mbText = customMaxFileSize
    ? `${customMaxFileSize} MB`
    : allowedMediaTypes?.includes(MediaType.VIDEO)
      ? `${MAX_VIDEO_FILE_SIZE} MB`
      : allowedMediaTypes?.includes(MediaType.IMAGE)
        ? `${MAX_IMAGE_FILE_SIZE} MB`
        : `${MAX_DOCUMENT_FILE_SIZE} MB`;

  if (errorCode === ErrorCode.FileTooLarge) {
    return toast.error(`The file size maximum limit is ${mbText}`);
  }

  return toast.error(errorMessage);
};

export const getFileMediaType = (file: File) => {
  if (file.type.startsWith("video/")) {
    return MediaType.VIDEO;
  }

  if (file.type.startsWith("image/")) {
    return MediaType.IMAGE;
  }

  if (file.type.startsWith("audio/")) {
    return MediaType.AUDIO;
  }

  return MediaType.DOCUMENT;
};

export const validateVideoFiles = async (videoFiles: FileWithMetadata[]) => {
  if (videoFiles.some((video) => video.size > MAX_VIDEO_FILE_SIZE)) {
    toast.error(
      `The video file size maximum limit is ${MAX_VIDEO_FILE_SIZE}MB`,
    );
    return false;
  }

  if (
    videoFiles.some(
      (video) =>
        video.duration && video.duration >= VIDEO_DURATION_LIMIT_IN_SECONDS,
    )
  ) {
    toast.error("Video uploads must be less than 1 minute in duration");

    return false;
  }

  return true;
};

export const getImageDimensions = async (
  file: File,
): Promise<{ width: number; height: number }> => {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export const getFileWithDimensions = async (
  file: File,
): Promise<File & { width?: number; height?: number }> => {
  try {
    if (file.type.startsWith("image/")) {
      const { width, height } = await getImageDimensions(file);
      return Object.assign(file, { width, height });
    } else if (file.type.startsWith("video/")) {
      const { width, height } = await getVideoMetadata(file);
      return Object.assign(file, { width, height });
    }

    return file;
  } catch {
    return file;
  }
};
